import { Component, OnDestroy, ViewChild, ElementRef, AfterViewInit, Input, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ImageBlob } from '../../base/model/features-decl';
import { ValidationService } from '../../base/validation/validation.service';
import { AppContext, WizardContext } from '../../base/control/context';
import { TabletSignEditor } from './tablet-sign-editor';

export abstract class TabletSignConfig {
  abstract useMouse(): boolean;
}

class CanvasInputtingEventArgs {
  x: number;
  y: number;
  type: 'UNDEFINED' | 'NEW_POINT' | 'START_DRAW' | 'STOP_DRAW';

  static fromMouseEvent(t: 'UNDEFINED' | 'NEW_POINT' | 'START_DRAW' | 'STOP_DRAW', ev: MouseEvent): CanvasInputtingEventArgs {
    return { x: ev.offsetX, y: ev.offsetY, type: t };
  }

  static fromTouchEvent(t: 'UNDEFINED' | 'NEW_POINT' | 'START_DRAW' | 'STOP_DRAW', ev: PointerEvent): CanvasInputtingEventArgs[] {
    const result: CanvasInputtingEventArgs[] = [];
    if (ev.type === 'pointermove') {
      const touches = (ev as any).getCoalescedEvents() as PointerEvent[];
      for (const touch of touches) {
        if (touch.isPrimary && touch.target instanceof HTMLElement) {
          const rect = touch.target.getBoundingClientRect();
          const x = (touch.pageX - rect.left) * (709 / rect.width);
          const y = (touch.pageY - rect.top) * (142 / rect.height);
          result.push({ x: x, y: y, type: t });
        }
      }
    }
    if (!result.length) {
      result.push({ x: -1, y: -1, type: t });
    }
    return result;
  }
}

interface ICanvasInputtingEventsProcessingStrategy {
  onMouseDown(ev: MouseEvent): void;
  onMouseUp(ev: MouseEvent): void;
  onMouseMove(ev: MouseEvent): void;
  onMouseLave(ev: MouseEvent): void;
  onTouchStart(ev: PointerEvent): void;
  onTouchEnd(ev: PointerEvent): void;
  onTouchMove(ev: PointerEvent): void;
  onTouchCancel(ev: PointerEvent): void;
}

@Component({
  selector: 'i-tablet-sign',
  templateUrl: './tablet-sign.component.html',
  styleUrls: ['./tablet-sign.component.scss'],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => TabletSignComponent), multi: true },
    { provide: TabletSignEditor, useClass: TabletSignEditor },
  ],
})
export class TabletSignComponent implements OnDestroy, AfterViewInit, ControlValueAccessor {
  private _canvasInputtingEventsSubj = new Subject<CanvasInputtingEventArgs>();
  private _canvasInputtingEventsProcessingStrategy: ICanvasInputtingEventsProcessingStrategy;

  private MouseCanvasInputtingEventsProcessingStrategy: ICanvasInputtingEventsProcessingStrategy = new (class
    implements ICanvasInputtingEventsProcessingStrategy
  {
    constructor(private _outputSubj: Subject<CanvasInputtingEventArgs>) {}

    onMouseDown(ev: MouseEvent): void {
      ev.preventDefault();
      this._outputSubj.next(CanvasInputtingEventArgs.fromMouseEvent('START_DRAW', ev));
    }

    onMouseLave(ev: MouseEvent): void {
      ev.preventDefault();
      this._outputSubj.next(CanvasInputtingEventArgs.fromMouseEvent('STOP_DRAW', ev));
    }

    onMouseMove(ev: MouseEvent): void {
      ev.preventDefault();
      this._outputSubj.next(CanvasInputtingEventArgs.fromMouseEvent('NEW_POINT', ev));
    }

    onMouseUp(ev: MouseEvent): void {
      ev.preventDefault();
      this._outputSubj.next(CanvasInputtingEventArgs.fromMouseEvent('STOP_DRAW', ev));
    }

    onTouchCancel(ev: PointerEvent): void {}
    onTouchEnd(ev: PointerEvent): void {}
    onTouchMove(ev: PointerEvent): void {}
    onTouchStart(ev: PointerEvent): void {}
  })(this._canvasInputtingEventsSubj);

  private TouchCanvasInputtingEventsProcessingStrategy: ICanvasInputtingEventsProcessingStrategy = new (class
    implements ICanvasInputtingEventsProcessingStrategy
  {
    constructor(private _outputSubj: Subject<CanvasInputtingEventArgs>) {}

    onMouseDown(ev: MouseEvent): void {}
    onMouseLave(ev: MouseEvent): void {}
    onMouseMove(ev: MouseEvent): void {}
    onMouseUp(ev: MouseEvent): void {}

    onTouchCancel(ev: PointerEvent): void {
      ev.preventDefault();
      this._outputSubj.next(CanvasInputtingEventArgs.fromTouchEvent('STOP_DRAW', ev)[0]);
    }

    onTouchEnd(ev: PointerEvent): void {
      ev.preventDefault();
      this._outputSubj.next(CanvasInputtingEventArgs.fromTouchEvent('STOP_DRAW', ev)[0]);
    }

    onTouchMove(ev: PointerEvent): void {
      ev.preventDefault();
      CanvasInputtingEventArgs.fromTouchEvent('NEW_POINT', ev).forEach((x) => this._outputSubj.next(x));
    }

    onTouchStart(ev: PointerEvent): void {
      ev.preventDefault();
      this._outputSubj.next(CanvasInputtingEventArgs.fromTouchEvent('START_DRAW', ev)[0]);
    }
  })(this._canvasInputtingEventsSubj);

  @Input() readonly: boolean = false;
  @Input() flags: ImageBlob;
  @Input() isInDebugMode: boolean = false;
  @ViewChild('canvas') canvasElement: ElementRef<HTMLCanvasElement>;

  debugPtsJson: string = '';
  private debugPts: CanvasInputtingEventArgs[] = [];
  private startImage: string;
  private tableSignState: TableSignState = TableSignState.Ready;
  private ctx: CanvasRenderingContext2D | null = null;
  private currX = -1;
  private currY = -1;

  constructor(
    private validationService: ValidationService,
    private tabletSignConfig: TabletSignConfig,
    private appContext: AppContext,
    private wizardContext: WizardContext,
    public tabletSignEditor: TabletSignEditor,
  ) {}

  get isReady(): boolean {
    return this.tableSignState === TableSignState.Ready;
  }

  get getWorking(): boolean {
    return this.tableSignState == TableSignState.DrawingMode || this.tableSignState == TableSignState.ErasingMode;
  }

  get isPhotoWizardVisible(): boolean {
    return this.tableSignState === TableSignState.PhotoWizard;
  }

  get getDrawingMode(): boolean {
    return this.tableSignState === TableSignState.DrawingMode;
  }

  get getEraseMode(): boolean {
    return this.tableSignState === TableSignState.ErasingMode;
  }

  private onChange: (arg: any) => void;

  registerOnChange(onChange: (arg: any) => void) {
    this.onChange = onChange;
  }

  registerOnTouched() {}

  setDisabledState() {}

  writeValue(obj: any) {
    if (typeof obj === 'string') {
      this.tableSignState = TableSignState.Ready;
      if (obj) {
        const img = new Image();
        img.src = obj;
        img.onload = () => {
          this.startImage = img.src;
          this.canvas$.getContext('2d')?.drawImage(img, 0, 0);
        };
      } else {
        const img = new Image();
        img.src = this.tabletSignEditor.clearImage;
        img.onload = () => {
          this.startImage = img.src;
          this.canvas$.getContext('2d')?.drawImage(img, 0, 0);
        };
      }
    }
  }

  private get canvas$(): HTMLCanvasElement {
    return this.canvasElement?.nativeElement;
  }

  private prepareContext() {
    this.ctx = this.canvas$.getContext('2d');
    this.ctx!.strokeStyle = 'black';
    this.ctx!.lineWidth = 2;
    this.ctx!.lineCap = 'round';
    this.ctx!.lineJoin = 'round';
  }

  ngAfterViewInit() {
    let inputtingStream = this.isInDebugMode
      ? this._canvasInputtingEventsSubj.pipe(tap((_) => this.debugPts.push(_)))
      : this._canvasInputtingEventsSubj;
    inputtingStream.subscribe({
      next: (e) => {
        switch (e.type) {
          case 'NEW_POINT':
            this.paint(e);
            break;
          case 'STOP_DRAW':
            this.stopDraw(e);
            break;
          case 'START_DRAW':
            this.startDraw(e);
        }
      },
    });
    this._canvasInputtingEventsProcessingStrategy = this.tabletSignConfig.useMouse()
      ? this.MouseCanvasInputtingEventsProcessingStrategy
      : this.TouchCanvasInputtingEventsProcessingStrategy;
    this.addListeners();
  }

  ngOnDestroy() {
    this.removeListeners();
  }

  private processDrawing: boolean = false;
  private mouseDownListener = (ev: MouseEvent) => {
    this.processDrawing = true;
    this._canvasInputtingEventsProcessingStrategy.onMouseDown(ev);
  };
  private mouseUpListener = (ev: MouseEvent) => {
    this.processDrawing = false;
    this._canvasInputtingEventsProcessingStrategy.onMouseUp(ev);
  };
  private mouseMoveListener = (ev: MouseEvent) => {
    this._canvasInputtingEventsProcessingStrategy.onMouseMove(ev);
  };
  private mouseLeaveListener = (ev: MouseEvent) => {
    this.processDrawing = false;
    this._canvasInputtingEventsProcessingStrategy.onMouseLave(ev);
  };
  private touchStartListener = (ev: PointerEvent) => {
    this.processDrawing = true;
    this._canvasInputtingEventsProcessingStrategy.onTouchStart(ev);
  };
  private touchEndListener = (ev: PointerEvent) => {
    this.processDrawing = false;
    this._canvasInputtingEventsProcessingStrategy.onTouchEnd(ev);
  };
  private touchMoveListener = (ev: PointerEvent) => {
    this._canvasInputtingEventsProcessingStrategy.onTouchMove(ev);
  };
  private touchCancelListener = (ev: PointerEvent) => {
    this._canvasInputtingEventsProcessingStrategy.onTouchCancel(ev);
  };

  private addListeners() {
    this.canvas$.addEventListener('mousedown', this.mouseDownListener);
    this.canvas$.addEventListener('mouseup', this.mouseUpListener);
    this.canvas$.addEventListener('mousemove', this.mouseMoveListener);
    this.canvas$.addEventListener('mouseleave', this.mouseLeaveListener);
    this.canvas$.addEventListener('pointerdown', this.touchStartListener);
    this.canvas$.addEventListener('pointerup', this.touchEndListener);
    this.canvas$.addEventListener('pointermove', this.touchMoveListener);
    this.canvas$.addEventListener('pointercancel', this.touchCancelListener);
  }

  private removeListeners() {
    this.canvas$.removeEventListener('mousedown', this.mouseDownListener);
    this.canvas$.removeEventListener('mouseup', this.mouseUpListener);
    this.canvas$.removeEventListener('mousemove', this.mouseMoveListener);
    this.canvas$.removeEventListener('mouseleave', this.mouseLeaveListener);
    this.canvas$.removeEventListener('pointerdown', this.touchStartListener);
    this.canvas$.removeEventListener('pointerup', this.touchEndListener);
    this.canvas$.removeEventListener('pointermove', this.touchMoveListener);
    this.canvas$.removeEventListener('pointercancel', this.touchCancelListener);
  }

  beginDrawing(ignoreState?: boolean) {
    if (!ignoreState) {
      this.tableSignState = TableSignState.DrawingMode;
    }
    this.debugPtsJson = '';
    this.debugPts = [];
    this.prepareContext();
    this.tabletSignEditor.runEditor(this.startImage, this.ctx!, this.canvas$.width, this.canvas$.height);
  }

  endDrawing() {
    this.tableSignState = TableSignState.Ready;
    this.debugPtsJson = JSON.stringify(this.debugPts);
    this.onChange(this.canvas$.toDataURL());
    this.flags.scanned = true;
    this.validationService.validate();
  }

  private startDraw(e: CanvasInputtingEventArgs) {
    if (this.getWorking && this.tableSignState != TableSignState.ErasingMode) {
      this.ctx!.strokeStyle = 'black';
      this.ctx!.lineWidth = 2;
    }
    if (this.getWorking) {
      this.paint(e, true);
    }
  }

  private stopDraw(e: CanvasInputtingEventArgs) {
    if (this.getWorking) this.paint(e);
  }

  private paint(e: CanvasInputtingEventArgs, isNewPath = false) {
    if (this.processDrawing) {
      this.paintPerform(e, isNewPath);
    }
  }

  private paintPerform(e: CanvasInputtingEventArgs, isNewPath = false) {
    let mode: string = this.tableSignState == TableSignState.DrawingMode ? 'drawing' : 'erase';
    if (this.getWorking && !this.tabletSignEditor.getDashState) {
      if (isNewPath) {
        this.ctx!.beginPath();
        this.currX = -1;
        this.currY = -1;
      }
      if (e.x >= 0 && e.y >= 0) {
        if (this.currX < 0 || this.currY < 0) {
          this.ctx!.moveTo(e.x, e.y);
          this.currX = e.x;
          this.currY = e.y;
        } else if (Math.abs(e.x - this.currX) > 4 || Math.abs(e.y - this.currY) > 4) {
          this.tabletSignEditor.line(this.currX, this.currY, e.x, e.y, mode);
          this.ctx!.lineTo(e.x, e.y);
          this.ctx!.stroke();
          this.ctx!.beginPath();
          this.ctx!.moveTo(e.x, e.y);
          this.currX = e.x;
          this.currY = e.y;
        }
      }
    }
  }

  toogleDrawErase(mode: string) {
    this.ctx!.strokeStyle = mode == 'drawing' ? 'black' : 'white';
    this.ctx!.lineWidth = mode == 'drawing' ? 2 : 16;
    this.tableSignState = mode == 'drawing' ? TableSignState.DrawingMode : TableSignState.ErasingMode;
  }

  private onEndPhotoMode: () => void;

  private startPhotoMode(onEndPhotoMode: () => void) {
    this.onEndPhotoMode = onEndPhotoMode;
    this.tableSignState = TableSignState.PhotoWizard;
    this.appContext.changeMessageSinkVisibleRef(false);
    this.wizardContext.setToolbarVisible(false);
    this.removeListeners();
  }

  onWizardClosure(img: string | null) {
    this.tableSignState = TableSignState.DrawingMode;
    this.appContext.changeMessageSinkVisibleRef(true);
    this.wizardContext.setToolbarVisible(true);
    setTimeout(() => {
      this.addListeners();
      this.prepareContext();
      this.tabletSignEditor.updateCtx(this.ctx!);
      this.tabletSignEditor.receivedNewPhoto(img, () => {
        this.onEndPhotoMode();
      });
    }, 1);
  }

  uploadImage($event: any) {
    const file: File = $event.target.files[0];
    const reader = new FileReader();
    reader.onload = () => {
      const img = reader.result;
      if (typeof img === 'string') {
        this.tabletSignEditor.receivedNewPhoto(img, () => {});
      }
    };
    if (file) {
      reader.readAsDataURL(file);
    }
  }

  clearCanvas() {
    if (this.tableSignState === TableSignState.Ready) {
      this.beginDrawing(true);
      this.tabletSignEditor.eraseCanvas(() => {
        this.endDrawing();
      });
    } else {
      this.tabletSignEditor.eraseCanvas(() => {});
    }
  }

  dashCanvas() {
    if (this.tableSignState === TableSignState.Ready) {
      this.beginDrawing(true);
      this.tabletSignEditor.dashCanvas(() => {
        this.endDrawing();
      });
    } else {
      this.tabletSignEditor.dashCanvas(() => {});
    }
  }

  photoCanvas() {
    if (this.tableSignState === TableSignState.Ready) {
      this.beginDrawing(true);
      this.startPhotoMode(() => {
        this.endDrawing();
      });
    } else {
      this.startPhotoMode(() => {});
    }
  }
}

export enum TableSignState {
  Ready,
  DrawingMode,
  ErasingMode,
  PhotoWizard,
}
